// =============================================================================
// Lese-Client fuer Directus
// =============================================================================
//
// Bewusst keine Abhaengigkeit auf @directus/sdk: Wir brauchen genau eine
// Sache - lesende GET-Anfragen auf vier Collections. Das SDK kann Schreiben,
// Anmelden, Echtzeit und Dateiverwaltung, und nichts davon soll diese App
// koennen. Was nicht da ist, kann auch niemand versehentlich benutzen.
//
// Alle Anfragen sind ANONYM. Es wird kein Token mitgeschickt - weder der
// Rollentoken aus dem Link noch ein Directus-Token. Der Lesezugriff laeuft
// ueber die oeffentlichen Leserechte, die Schritt 3 eingerichtet hat.
// =============================================================================

import { DIRECTUS_URL } from '../konfiguration';

/** Fehler, die wir dem Benutzer erklaeren koennen - im Unterschied zu einem
 *  unerwarteten Absturz. Die Art bestimmt, welcher Text erscheint. */
export type FehlerArt =
  | 'keine-verbindung' // Directus antwortet nicht
  | 'kein-zugriff' // 401/403 - oeffentliche Leserechte fehlen
  | 'antwort-unbrauchbar'; // erreichbar, aber die Antwort passt nicht

export class DirectusFehler extends Error {
  readonly art: FehlerArt;
  readonly technisch: string;

  constructor(art: FehlerArt, technisch: string) {
    super(technisch);
    this.name = 'DirectusFehler';
    this.art = art;
    this.technisch = technisch;
  }
}

interface AntwortHuelle<T> {
  data?: T;
  errors?: { message?: string }[];
}

/**
 * Eine lesende Abfrage auf eine Collection.
 *
 * @param collection  phasen, termine, glossar oder dokumente
 * @param felder      Feldliste fuer den fields-Parameter, inklusive Verweisen
 * @param zusatz      weitere Abfrageparameter, etwa sort
 */
export async function lese<T>(
  collection: string,
  felder: string[],
  zusatz: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<T[]> {
  const parameter = new URLSearchParams({
    fields: felder.join(','),
    // -1 heisst "alle". Die Datenmengen sind winzig: sechs Phasen, ein paar
    // Dutzend Termine. Seitenweises Nachladen waere Aufwand ohne Nutzen.
    limit: '-1',
    ...zusatz,
  });

  const adresse = `${DIRECTUS_URL}/items/${collection}?${parameter.toString()}`;

  let antwort: Response;
  try {
    antwort = await fetch(adresse, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      // Ausdruecklich keine Anmeldedaten: keine Cookies, kein Token.
      credentials: 'omit',
      ...(signal ? { signal } : {}),
    });
  } catch (fehler) {
    // Netzwerkfehler, DNS, abgelehnte Verbindung - und auch der Fall, dass
    // CORS die Antwort blockiert. Von hier aus sind die nicht unterscheidbar.
    throw new DirectusFehler(
      'keine-verbindung',
      `${collection}: ${fehler instanceof Error ? fehler.message : String(fehler)}`,
    );
  }

  if (antwort.status === 401 || antwort.status === 403) {
    throw new DirectusFehler(
      'kein-zugriff',
      `${collection}: HTTP ${antwort.status} - oeffentliche Leserechte fehlen`,
    );
  }

  if (!antwort.ok) {
    throw new DirectusFehler(
      'antwort-unbrauchbar',
      `${collection}: HTTP ${antwort.status} ${antwort.statusText}`,
    );
  }

  let huelle: AntwortHuelle<unknown>;
  try {
    huelle = (await antwort.json()) as AntwortHuelle<unknown>;
  } catch {
    throw new DirectusFehler('antwort-unbrauchbar', `${collection}: keine gueltige JSON-Antwort`);
  }

  if (huelle.errors !== undefined && huelle.errors.length > 0) {
    const erste = huelle.errors[0]?.message ?? 'unbekannter Fehler';
    throw new DirectusFehler('antwort-unbrauchbar', `${collection}: ${erste}`);
  }

  if (!Array.isArray(huelle.data)) {
    throw new DirectusFehler(
      'antwort-unbrauchbar',
      `${collection}: erwartet wurde eine Liste, bekommen ${typeof huelle.data}`,
    );
  }

  return huelle.data as T[];
}

/** Adresse einer hochgeladenen Datei. Directus prueft auch hier die
 *  oeffentlichen Leserechte - ohne Recht auf directus_files kaeme 403. */
export function dateiAdresse(dateiId: string, dateiname?: string): string {
  const zusatz = dateiname === undefined ? '' : `?download=&filename=${encodeURIComponent(dateiname)}`;
  return `${DIRECTUS_URL}/assets/${dateiId}${zusatz}`;
}
